import { authFetch } from './auth';
import { downloadJson } from './downloadJson';

/**
 * Run the full GDPR data export (POST /api/account/export) and download the
 * result. One shared implementation so every entry point (Coord minner,
 * Kontoinnstillinger) gets the same endpoint, filename and failure handling
 * — the previous per-page copies drifted apart.
 *
 * The backend reads all seven stores and reports per-store outcomes in
 * `data.manifest`; a partial failure still returns 200 with the readable
 * stores, so the file is downloaded and the failed sections are surfaced to
 * the caller for a visible notice.
 *
 * Never throws. Returns:
 * - { status: 'ok' }                          — full export downloaded
 * - { status: 'partial', failedSections }     — file downloaded, but these
 *     sections (Norwegian descriptions from the manifest) are missing
 * - { status: 'error' }                       — nothing downloaded
 */
export async function exportAllUserData() {
  try {
    const response = await authFetch('/account/export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ format: 'json' })
    });
    if (!response.ok) {
      return { status: 'error', failedSections: [] };
    }

    const body = await response.json();
    if (!body?.success || !body.data) {
      return { status: 'error', failedSections: [] };
    }

    downloadJson(
      body.data,
      `coord-data-export-${new Date().toISOString().split('T')[0]}.json`
    );

    const failed = Array.isArray(body.data.manifest)
      ? body.data.manifest.filter(entry => entry.status === 'failed')
      : [];
    if (failed.length > 0) {
      return {
        status: 'partial',
        failedSections: failed.map(entry => entry.description || entry.store)
      };
    }
    return { status: 'ok', failedSections: [] };
  } catch (error) {
    console.error('Error exporting account data:', error);
    return { status: 'error', failedSections: [] };
  }
}
